const MiB = 1024 * 1024;
export interface RestoreProgressPresentation {
  percent: number | null;
  heading: string;
  detail: string;
  failed: boolean;
}

export const restoreIsActive = (input: {
  mediaStatus: string;
  storageStatus: string;
}): boolean => input.mediaStatus === "restoring" || input.storageStatus === "restore_pending";

export const formatRestoreBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 MB";
  const gibibytes = bytes / (1024 ** 3);
  if (gibibytes >= 1) return `${gibibytes.toFixed(gibibytes >= 10 ? 1 : 2)} GB`;
  return `${Math.max(1, Math.round(bytes / MiB))} MB`;
};

export const restoreProgressPercent = (uploadedBytes: number, expectedSizeBytes: number): number | null => {
  if (!Number.isFinite(uploadedBytes) || !Number.isFinite(expectedSizeBytes) || expectedSizeBytes <= 0) return null;
  return Math.min(100, Math.max(0, Math.round((uploadedBytes / expectedSizeBytes) * 100)));
};

export const estimatedRestoreTransferMinutes = (uploadedBytes: number, expectedSizeBytes: number, bytesPerSecond?: number): number | null => {
  if (!Number.isFinite(uploadedBytes) || !Number.isFinite(expectedSizeBytes) || expectedSizeBytes <= 0
    || !Number.isFinite(bytesPerSecond) || (bytesPerSecond ?? 0) <= 0) return null;
  const remainingBytes = Math.max(0, expectedSizeBytes - uploadedBytes);
  if (remainingBytes === 0) return 0;
  return Math.max(1, Math.ceil(remainingBytes / (bytesPerSecond as number) / 60));
};

export const restoreProgressPresentation = (input: {
  status: string;
  uploadedBytes: number;
  expectedSizeBytes: number;
  bytesPerSecond?: number;
}): RestoreProgressPresentation => {
  const percent = restoreProgressPercent(input.uploadedBytes, input.expectedSizeBytes);
  if (input.status === "failed") {
    return { percent, heading: "Cloudflare Full Restore needs attention", detail: "The Full Restore paused before playback was ready. Try the restore again.", failed: true };
  }
  if (input.status === "processing" || (percent === 100 && input.status !== "ready")) {
    return { percent: 100, heading: "Cloudflare transfer complete", detail: "Cloudflare Full Restore View is preparing playback. This usually takes a few more minutes.", failed: false };
  }
  if (input.status === "ready") {
    return { percent: 100, heading: "Cloudflare Full Restore View is ready", detail: "Loading the restored video…", failed: false };
  }
  if (percent === null || input.status === "preparing") {
    return { percent: null, heading: "Preparing Cloudflare Full Restore", detail: "Creating a secure transfer from the Google archive…", failed: false };
  }
  const minutes = estimatedRestoreTransferMinutes(input.uploadedBytes, input.expectedSizeBytes, input.bytesPerSecond);
  const estimate = minutes === null
    ? "Estimating the remaining transfer time…"
    : `About ${minutes} minute${minutes === 1 ? "" : "s"} remaining, plus a few minutes to prepare playback.`;
  return {
    percent,
    heading: `Cloudflare Full Restore · ${percent}%`,
    detail: `${formatRestoreBytes(input.uploadedBytes)} of ${formatRestoreBytes(input.expectedSizeBytes)} transferred. ${estimate}`,
    failed: false,
  };
};
